import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { InputRef } from 'antd'
import { Button, Divider, Input, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { usePhoneVerification } from "../../contexts/PhoneVerificationContext";
import {
  formatRemainingLockTime,
  getInitialPhoneVerificationLockState,
  getNextVerificationFailureState,
  getRemainingLockTimeMs,
  getRemainingVerificationAttempts,
  isVerificationBlocked as getIsVerificationBlocked,
  MAX_VERIFICATION_FAILURES,
  persistPhoneVerificationLockState,
} from './phoneVerificationLock'

type Props = {
  tracking?: string;
  phonePrefix?: string;
  lastFourDigits?: string;
  onVerified: () => void;
}
export const PhoneVerification = ({
                                    tracking,
                                    phonePrefix,
                                    onVerified,
                                    lastFourDigits,
                                  }: Props) => {
  const { t } = useTranslation('tracking')
  const [phoneSuffix, setPhoneSuffix] = useState('')
  const [verificationError, setVerificationError] = useState('')
  const [currentTime, setCurrentTime] = useState(() => Date.now())
  const [lockState, setLockState] = useState(() => getInitialPhoneVerificationLockState(tracking, Date.now()))
  const modalRef = useRef<{ destroy: () => void; update: (config: Record<string, unknown>) => void } | null>(null)
  const inputRefs = useRef<Array<InputRef | null>>([])
  const phoneSuffixRef = useRef('')
  const { verifyTracking } = usePhoneVerification()
  const isVerificationBlocked = getIsVerificationBlocked(lockState.blockedUntil, currentTime)
  const failedAttempts = lockState.failedAttempts
  const remainingAttempts = getRemainingVerificationAttempts(failedAttempts)
  const remainingTime = getRemainingLockTimeMs(lockState.blockedUntil, currentTime)
  const remainingLockTime = formatRemainingLockTime(getRemainingLockTimeMs(lockState.blockedUntil, currentTime))

  phoneSuffixRef.current = phoneSuffix

  const destroy = useCallback(() => {
    modalRef.current?.destroy()
    modalRef.current = null
  }, [])

  const close = useCallback(() => {
    destroy()
  }, [destroy])

  const focusPhoneSuffixInput = useCallback(() => {
    window.setTimeout(() => {
      inputRefs.current[0]?.input?.focus()
      inputRefs.current[0]?.focus()
    }, 50)
  }, [])

  const handleVerifyPhoneSuffix = () => {
    if (isVerificationBlocked) {
      setVerificationError(t('phone.verification.locked'))
      return Promise.resolve()
    }

    if (phoneSuffixRef.current === lastFourDigits) {
      verifyTracking(tracking)
      setPhoneSuffix('')
      setVerificationError('')
      setLockState({ failedAttempts: 0, blockedUntil: null })
      destroy()
      onVerified()
      return Promise.resolve()
    }

    const nextFailureState = getNextVerificationFailureState(failedAttempts, Date.now())
    setLockState({
      failedAttempts: nextFailureState.failedAttempts,
      blockedUntil: nextFailureState.blockedUntil,
    })
    setPhoneSuffix('')

    if (nextFailureState.isBlocked) {
      setCurrentTime(Date.now())
      setVerificationError(t('phone.verification.locked'))
      return Promise.resolve()
    }

    setVerificationError(t('phone.verification.error'))
    return Promise.resolve()
  }

  const handlePhoneSuffixChange = (value: string) => {
    setPhoneSuffix(value.replace(/\D/g, ''))
    if (verificationError && !isVerificationBlocked) {
      setVerificationError('')
    }
  }

  const handleCodeDigitChange = (index: number, value: string) => {
    const nextDigit = value.replace(/\D/g, '').slice(-1)
    const nextDigits = phoneSuffix.padEnd(4, ' ').split('')
    nextDigits[index] = nextDigit || ''

    handlePhoneSuffixChange(nextDigits.join('').trim())

    if (nextDigit && index < 3) {
      inputRefs.current[index + 1]?.input?.focus()
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleCodeKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !phoneSuffix[index] && index > 0) {
      inputRefs.current[index - 1]?.input?.focus()
      inputRefs.current[index - 1]?.focus()
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      inputRefs.current[index - 1]?.input?.focus()
      inputRefs.current[index - 1]?.focus()
    }

    if (event.key === 'ArrowRight' && index < 3) {
      event.preventDefault()
      inputRefs.current[index + 1]?.input?.focus()
      inputRefs.current[index + 1]?.focus()
    }
  }

  const phoneDigits = phoneSuffix.padEnd(4, ' ').split('')
  const hasFailedAttempts = failedAttempts > 0 && failedAttempts < MAX_VERIFICATION_FAILURES
  const content = (
    <div className={"body"}>
      <div className={"title"}>{t("phone.verification.title")}</div>
      <div>{t('phone.verification.description')}</div>
      <div className={'phone-verification-code'}>
        <div className={'phone-verification-prefix'}>{phonePrefix || t('phone.verification.prefixFallback')}</div>
        <div className={'phone-verification-digits'}>
          {phoneDigits.map((digit, index) => (
            <Input
              key={index}
              ref={(element) => {
                inputRefs.current[index] = element
              }}
              size={"small"}
              maxLength={1}
              inputMode={'numeric'}
              autoComplete={'off'}
              disabled={isVerificationBlocked}
              value={digit.trim()}
              className={'phone-verification-digit'}
              aria-label={t('phone.verification.digitLabel', { index: index + 1 })}
              onPressEnter={handleVerifyPhoneSuffix}
              onChange={(event) => handleCodeDigitChange(index, event.target.value)}
              onKeyDown={(event) => handleCodeKeyDown(index, event)}
            />
          ))}
        </div>
      </div>
      {!!verificationError && <div
        className={`phone-verification-error${isVerificationBlocked ? ' phone-verification-error-blocked' : ''}`}>
        {verificationError}
        {isVerificationBlocked && (
          <div className={'phone-verification-error-secondary'}>
            {t('phone.verification.locked.min', formatRemainingLockTime(remainingTime))}
          </div>
        )}
        {hasFailedAttempts && <div>{t('phone.verification.remainingAttempts', { remainingAttempts })}</div>}
      </div>}
    </div>
  )

  const footer = (<div className={"footer"}>
    <Button type={"text"} className={"cancel"} onClick={close}>{t('phone.verification.cancel')}</Button>
    <Divider type={"vertical"} className={"divider"}/>
    <Button type={"text"} className={"ok"}
            disabled={isVerificationBlocked}
            onClick={handleVerifyPhoneSuffix}>{t('phone.verification.confirm')}</Button>
  </div>)

  useEffect(() => {
    if (!modalRef.current) {
      return
    }

    modalRef.current.update({
      content,
      footer,
    })
  }, [content, footer])

  useEffect(() => {
    if (!modalRef.current || !verificationError || isVerificationBlocked) {
      return
    }

    focusPhoneSuffixInput()
  }, [isVerificationBlocked, verificationError, focusPhoneSuffixInput])

  useEffect(() => {
    if (!lockState.blockedUntil) {
      return
    }


    if (remainingTime <= 0) {
      setLockState({ failedAttempts: 0, blockedUntil: null })
      setVerificationError('')
      return
    }

    setVerificationError(t('phone.verification.locked'))

    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now())
    }, 1000)

    const timeoutId = window.setTimeout(() => {
      setLockState({ failedAttempts: 0, blockedUntil: null })
      setVerificationError('')
      setCurrentTime(Date.now())
    }, remainingTime)

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [currentTime, lockState.blockedUntil, t])

  useEffect(() => {
    persistPhoneVerificationLockState(tracking, lockState)
  }, [lockState, tracking])

  useEffect(() => () => {
    destroy()
  }, [])

  const open = useCallback(() => {
    destroy()
    setPhoneSuffix('')
    setCurrentTime(Date.now())
    setVerificationError(isVerificationBlocked ? t('phone.verification.locked') : '')

    modalRef.current = Modal.confirm({
      autoFocusButton: null,
      icon: <div/>,
      closable: false,
      rootClassName: "sm-modal md",
      classNames: { content: "content", footer: "footer" },
      centered: true,
      content,
      footer,
      maskClosable: false
    })

    focusPhoneSuffixInput()
  }, [content, destroy, focusPhoneSuffixInput, footer, isVerificationBlocked, remainingLockTime, t])

  return (<Button type={"primary"} style={{ width: 77 }} onClick={open}>{t("show")}</Button>)
}
